import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { Mascot } from '@/components/ui/Mascot';

/**
 * Детали мобильного макета (утверждён 23.08.2026).
 *
 * ЗАЧЕМ ОБЩИЙ НАБОР. В макете восемь экранов, и они собраны из одних и тех же
 * четырёх кирпичей: тонированный блок-список, оранжевый блок действия, полоска
 * вкладок и лента фишек. Если каждый экран будет описывать их своими классами,
 * то через месяц скругления и отступы разойдутся — а именно из-за них макет и
 * выглядит цельным.
 *
 * ПРАВИЛА МАКЕТА, зашитые здесь:
 *  • один крупный оранжевый объект на экран — остальное тонировано;
 *  • список — ОДИН блок с разделителями, а не плашка на каждую строку;
 *  • горизонтальная лента вместо сеток 3×2;
 *  • синий — состояние и вторичное, оранжевый — только действие.
 */

/* ───────────────────────────── заголовок экрана ───────────────────────────── */

export function MTitle({
  title,
  subtitle,
  back,
  right,
}: {
  title: string;
  subtitle?: string;
  back?: string;
  /** Действие справа от заголовка — «Прочитать все» в уведомлениях сотрудника. */
  right?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5 pb-1 pt-1.5">
      {back && (
        <Link
          to={back}
          aria-label={title}
          // 44 px — норма касания (решение владельца 13.10.2026: «назад» 44 px у всех ролей; было 36).
          className="grid h-11 w-11 flex-none place-items-center rounded-full bg-surface-container-low text-on-surface"
        >
          <span className="material-symbols-outlined text-[18px]">chevron_left</span>
        </Link>
      )}
      <div className="min-w-0 flex-1">
        <h1 className="font-display text-[22px] font-bold leading-tight text-on-surface">{title}</h1>
        {subtitle && <p className="mt-0.5 text-[11.5px] text-on-surface-variant">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

/**
 * Круглая оранжевая «+» справа в шапке экрана сотрудника (правило 3 дизайна, 13.10.2026): форма создания на телефоне
 * не раскрывается на странице, а открывается окном во весь экран по этой кнопке. 44 px; подпись — для читалки экрана.
 */
export function MAdd({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid h-11 w-11 flex-none place-items-center rounded-full bg-primary text-on-primary"
    >
      <span className="material-symbols-outlined text-[22px]">add</span>
    </button>
  );
}

export function MSection({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 px-0.5 pt-1">
      <h2 className="min-w-0 font-display text-[16px] font-bold text-on-surface">{title}</h2>
      {/* Метка справа не сжимается: рядом с длинным названием курса «Пройдено 3 из 3» ломалось в две строки (13.10.2026). */}
      {right && <span className="flex-none whitespace-nowrap text-[12px] font-semibold text-on-surface-variant">{right}</span>}
    </div>
  );
}

/* ───────────────────────────── полоска вкладок ───────────────────────────── */

export function MSegmented<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-pill bg-surface-container-low p-1">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={cn(
            'flex-1 rounded-pill px-1.5 py-2.5 text-[12.5px] font-semibold transition',
            active === tab.key ? 'bg-primary text-on-primary' : 'text-on-surface-variant',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────── оранжевый блок действия ─────────────────────────── */

export function MHero({
  eyebrow,
  title,
  subtitle,
  percent,
  action,
  to,
  onClick,
  right,
  mascot = 'hello',
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  percent?: number;
  action?: string;
  to?: string;
  onClick?: () => void;
  /** Число справа от заголовка — процент курса в макете. */
  right?: string;
  /**
   * Поза маскота в углу блока. В макете он живёт ИМЕННО в оранжевом блоке —
   * заказчик 24.08.2026 заметил, что на «Оқу», тренажёрах и в словаре его нет.
   * `null` убирает его там, где справа стоит число (проценты курса).
   */
  mascot?: string | null;
  children?: ReactNode;
}) {
  const cta = action && (
    <span className="mt-3 inline-flex w-max items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[14px] font-bold text-primary-pressed">
      {action}
      <span className="material-symbols-outlined text-[16px]">arrow_forward</span>
    </span>
  );
  return (
    <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-primary to-primary-hover px-[18px] pb-3.5 pt-4 text-on-primary">
      <div className="flex items-start gap-3">
        {/* Отступ под маскота — только там, где он есть: в store-сборке маскота нет (D76, Mascot.tsx). */}
        <div className={cn('min-w-0 flex-1', mascot && !right && !IS_APP_BUILD && 'pr-20')}>
          {eyebrow && (
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-90">{eyebrow}</div>
          )}
          <div className="mt-1.5 font-display text-[21px] font-bold leading-[1.1] text-white">{title}</div>
          {subtitle && <div className="mt-1 text-[12px] opacity-95">{subtitle}</div>}
        </div>
        {right && <div className="font-display text-[20px] font-bold text-white">{right}</div>}
      </div>
      {typeof percent === 'number' && (
        <div className="my-3 h-1.5 overflow-hidden rounded-full bg-white/35">
          <div className="h-full rounded-full bg-white" style={{ width: `${Math.min(100, percent)}%` }} />
        </div>
      )}
      {children}
      {to ? <Link to={to}>{cta}</Link> : onClick ? <button type="button" onClick={onClick}>{cta}</button> : cta}
      {mascot && (
        <Mascot
          pose={mascot}
          className="pointer-events-none absolute -bottom-1 -right-2 h-[118px] w-[104px] object-contain"
        />
      )}
    </div>
  );
}

/* ──────────────────────── тонированный блок-список ────────────────────────── */

export function MBlock({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('rounded-[26px] bg-surface-container-low px-4', className)}>{children}</div>;
}

/**
 * Строка списка. Разделитель рисует сама строка, кроме последней — так список
 * остаётся одним блоком, а не набором плашек.
 */
export function MRow({
  leading,
  title,
  meta,
  metaAccent,
  trailing,
  aside,
  below,
  id,
  to,
  onClick,
  last,
  dim,
  wrap,
}: {
  leading?: ReactNode;
  title: ReactNode;
  /**
   * Название в две строки, а не обрезка в одну: у сотрудника название урока и курса — главное в строке, а обрезанное
   * до «Lesson 3. Present Simp…» не отличить от соседнего (docs/CURATOR-MOBILE-DESIGN.md §3, правило 1).
   */
  wrap?: boolean;
  meta?: ReactNode;
  /** Подпись оранжевым — «сейчас», «до 23:59» и прочая срочность. */
  metaAccent?: boolean;
  trailing?: ReactNode;
  /**
   * Кнопки справа, ВНЕ ссылки строки (чип оценки урока, D11). `trailing` лежит
   * внутри ссылки — кнопка там уводила бы по ссылке вместо своего действия.
   */
  aside?: ReactNode;
  /** Раскрытие под строкой (фидбек куратора), внутри той же ячейки списка. */
  below?: ReactNode;
  /** Якорь строки: докрутить до урока, открытого из уведомления. */
  id?: string;
  to?: string;
  onClick?: () => void;
  last?: boolean;
  /** Недоступное: закрытый урок, завершённое занятие. */
  dim?: boolean;
}) {
  const titleClass = cn(
    'text-[14px] font-semibold text-on-surface',
    wrap ? 'line-clamp-2 leading-[1.3]' : 'truncate',
  );
  if (aside !== undefined || below !== undefined) {
    const content = (
      <div className="flex min-w-0 flex-1 items-center gap-3 py-3">
        {leading}
        <div className="min-w-0 flex-1">
          <div className={titleClass}>{title}</div>
          {meta && (
            <div
              className={cn(
                'truncate text-[11.5px]',
                metaAccent ? 'text-on-primary-container' : 'text-on-surface-variant',
              )}
            >
              {meta}
            </div>
          )}
        </div>
      </div>
    );
    const tail = trailing ?? (to ? (
      <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">chevron_right</span>
    ) : null);
    return (
      <div id={id} className={cn(!last && 'border-b border-outline', dim && 'opacity-55')}>
        <div className="flex items-center gap-1">
          {to ? (
            <Link to={to} className="flex min-w-0 flex-1">
              {content}
            </Link>
          ) : onClick ? (
            // Строка с кнопками справа и действием по нажатию (правка правила в «Заданиях», 13.10.2026): до этого
            // `onClick` здесь молча пропадал — нажималась только ссылка `to`.
            <button type="button" onClick={onClick} className="flex min-w-0 flex-1 text-left">
              {content}
            </button>
          ) : (
            content
          )}
          {aside}
          {/* Стрелка после кнопок — тот же переход; второй раз читалке экрана
              его не объявляем. */}
          {tail && to ? (
            <Link to={to} tabIndex={-1} aria-hidden="true" className="grid flex-none place-items-center self-stretch">
              {tail}
            </Link>
          ) : (
            tail
          )}
        </div>
        {below}
      </div>
    );
  }
  const body = (
    <div
      id={id}
      className={cn(
        'flex items-center gap-3 py-3',
        !last && 'border-b border-outline',
        dim && 'opacity-55',
      )}
    >
      {leading}
      <div className="min-w-0 flex-1">
        <div className={titleClass}>{title}</div>
        {meta && (
          <div
            className={cn(
              'truncate text-[11.5px]',
              metaAccent ? 'text-on-primary-container' : 'text-on-surface-variant',
            )}
          >
            {meta}
          </div>
        )}
      </div>
      {trailing ?? (
        (to || onClick) && (
          <span className="material-symbols-outlined flex-none text-[17px] text-on-surface-variant">
            chevron_right
          </span>
        )
      )}
    </div>
  );
  if (to) return <Link to={to} className="block">{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="block w-full text-left">{body}</button>;
  return body;
}

/** Кружок-значок слева в строке: синий для состояния, тёплый для действия. */
export function MIcon({ icon, tone = 'info' }: { icon: string; tone?: 'info' | 'warm' | 'done' | 'muted' }) {
  const tones = {
    info: 'bg-info-container text-info',
    warm: 'bg-primary-container text-primary',
    done: 'bg-info text-white',
    muted: 'bg-surface-container-high text-on-surface-variant',
  } as const;
  return (
    <span className={cn('grid h-[34px] w-[34px] flex-none place-items-center rounded-xl', tones[tone])}>
      <span className="material-symbols-outlined text-[17px]">{icon}</span>
    </span>
  );
}

/** Метка состояния: «меңгерілді», «жаңа», «дайын». */
export function MTag({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warm' | 'muted' }) {
  const tones = {
    info: 'bg-info-container text-info',
    warm: 'bg-primary-container text-on-primary-container',
    muted: 'bg-surface-container-high text-on-surface-variant',
  } as const;
  return (
    <span className={cn('flex-none rounded-full px-2 py-1 text-[10px] font-bold uppercase', tones[tone])}>
      {children}
    </span>
  );
}

/* ───────────────────────────── лента фишек ────────────────────────────────── */

export function MChips<T extends string>({
  chips,
  active,
  onChange,
}: {
  chips: { key: T; label: string }[];
  active: T;
  onChange: (k: T) => void;
}) {
  return (
    <div className="-mx-5 flex gap-1.5 overflow-x-auto px-5 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => onChange(chip.key)}
          className={cn(
            'flex-none rounded-full px-3.5 py-2 text-[12px] font-semibold transition',
            active === chip.key ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface',
          )}
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Четыре числа в ряд — «сөздер бойынша прогресс» и подобное. `cols={3}` — три числа на всю ширину (карточка ученика
 * у куратора: баллы, активность, курсы); без него ряд прежний, на четыре.
 */
export function MStats({
  items,
  cols = 4,
}: {
  items: { value: ReactNode; label: string; tone?: 'plain' | 'info' | 'warm' }[];
  cols?: 3 | 4;
}) {
  return (
    <div className="rounded-[26px] bg-surface-container-low px-4 py-3.5">
      <div className={cn('grid gap-1.5', cols === 3 ? 'grid-cols-3' : 'grid-cols-4')}>
        {items.map((it) => (
          <div key={it.label}>
            <div
              className={cn(
                'font-display text-[19px] font-bold',
                it.tone === 'info' ? 'text-info' : it.tone === 'warm' ? 'text-on-primary-container' : 'text-on-surface',
              )}
            >
              {it.value}
            </div>
            <div className="text-[10.5px] text-on-surface-variant">{it.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Синяя строка-ссылка в конце экрана: материалы, куратор, цель. */
export function MNote({
  icon,
  title,
  meta,
  to,
  onClick,
  trailing,
}: {
  icon?: string;
  title: string;
  meta?: string;
  to?: string;
  onClick?: () => void;
  trailing?: ReactNode;
}) {
  const body = (
    <div className="flex items-center gap-3 rounded-[22px] bg-info-container px-4 py-3.5">
      {icon && (
        <span className="grid h-8 w-8 flex-none place-items-center rounded-xl bg-white text-info">
          <span className="material-symbols-outlined text-[17px]">{icon}</span>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13.5px] font-semibold text-on-surface">{title}</div>
        {meta && <div className="truncate text-[11.5px] text-on-surface-variant">{meta}</div>}
      </div>
      {trailing ?? (
        (to || onClick) && (
          <span className="material-symbols-outlined flex-none text-[17px] text-info">chevron_right</span>
        )
      )}
    </div>
  );
  if (to) return <Link to={to}>{body}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="block w-full text-left">{body}</button>;
  return body;
}

/** Пустой экран: пунктирная рамка, кружок со значком, объяснение. */
export function MEmpty({ icon, title, hint }: { icon: string; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 rounded-[26px] border-2 border-dashed border-outline px-6 py-6 text-center">
      <span className="grid h-14 w-14 place-items-center rounded-full bg-surface-container-low p-3 text-on-surface-variant">
        <span className="material-symbols-outlined text-[24px]">{icon}</span>
      </span>
      <div className="text-[14.5px] font-semibold text-on-surface">{title}</div>
      {hint && <div className="max-w-[230px] text-[11.5px] text-on-surface-variant">{hint}</div>}
    </div>
  );
}

/* ──────────────────────── подпись раздела и выключатель ──────────────────── */

/**
 * Подпись раздела заглавными — «ИНТЕРФЕЙС ТІЛІ», «ХАБАРЛАНДЫРУЛАР».
 * В макете такие подписи разделяют экран настроек вместо заголовков карточек.
 */
export function MLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-0.5 pb-2 pt-4 text-[10.5px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
      {children}
    </div>
  );
}

/** Выключатель из макета: дорожка 44×26, кружок 20. */
export function MSwitch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  /** Нажать нельзя — например, уведомления запрещены в настройках телефона. */
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={cn(
        'flex h-[26px] w-11 flex-none rounded-full p-[3px] transition-colors disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-outline',
      )}
    >
      <span
        className={cn(
          'h-5 w-5 rounded-full bg-white shadow-soft transition-transform',
          checked && 'translate-x-[18px]',
        )}
      />
    </button>
  );
}
